import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  FileText, 
  CreditCard, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  LogOut,
  User,
  DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("client_token");
    const userData = localStorage.getItem("client_user");
    
    if (!token) {
      navigate("/portal");
      return;
    }

    setUser(JSON.parse(userData));
    fetchInvoices(token);
  }, [navigate]);

  const fetchInvoices = async (token) => {
    try {
      const response = await axios.get(`${API}/client/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("client_token");
    localStorage.removeItem("client_user");
    navigate("/portal");
  };

  const stats = {
    total: invoices.length,
    paid: invoices.filter(i => i.status === "paid").length,
    unpaid: invoices.filter(i => i.status === "unpaid").length,
    overdue: invoices.filter(i => i.status === "overdue").length,
    totalOwed: invoices.filter(i => i.status !== "paid").reduce((sum, i) => sum + i.amount, 0),
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-green-500/20 text-green-400 border-green-500/30",
      unpaid: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      overdue: "bg-red-500/20 text-red-400 border-red-500/30",
      cancelled: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    };
    return (
      <span className={`px-3 py-1 text-xs uppercase tracking-wider border ${styles[status]}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#2A2A2A] py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_1eeea6a6-945d-4052-ae21-011aad17a3a1/artifacts/qm929xya_fulllogo_transparent_nobuffer.png" 
                alt="Astute Builders" 
                className="h-10"
              />
            </Link>
            <div className="hidden sm:block">
              <h1 className="font-['Oswald'] text-xl text-white">Client Portal</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <User size={18} />
              <span className="hidden sm:inline">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="font-['Oswald'] text-3xl text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h2>
          <p className="text-[#A1A1AA]">Manage your invoices and payments</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#121212] border border-[#2A2A2A] p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText size={24} className="text-[#7ED4C6]" />
              <span className="text-[#A1A1AA] text-sm">Total Invoices</span>
            </div>
            <p className="font-['Oswald'] text-3xl text-white">{stats.total}</p>
          </div>
          <div className="bg-[#121212] border border-[#2A2A2A] p-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle size={24} className="text-green-400" />
              <span className="text-[#A1A1AA] text-sm">Paid</span>
            </div>
            <p className="font-['Oswald'] text-3xl text-white">{stats.paid}</p>
          </div>
          <div className="bg-[#121212] border border-[#2A2A2A] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock size={24} className="text-yellow-400" />
              <span className="text-[#A1A1AA] text-sm">Unpaid</span>
            </div>
            <p className="font-['Oswald'] text-3xl text-white">{stats.unpaid}</p>
          </div>
          <div className="bg-[#121212] border border-[#2A2A2A] p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign size={24} className="text-[#7ED4C6]" />
              <span className="text-[#A1A1AA] text-sm">Amount Due</span>
            </div>
            <p className="font-['Oswald'] text-3xl text-white">
              ${stats.totalOwed.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-[#121212] border border-[#2A2A2A]">
          <div className="p-6 border-b border-[#2A2A2A]">
            <h3 className="font-['Oswald'] text-xl text-white">Your Invoices</h3>
          </div>
          
          {invoices.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={48} className="text-[#2A2A2A] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No invoices yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1E1E1E]">
                  <tr>
                    <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Invoice #</th>
                    <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Description</th>
                    <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Amount</th>
                    <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Due Date</th>
                    <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Status</th>
                    <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-t border-[#2A2A2A] hover:bg-[#1E1E1E]">
                      <td className="p-4 text-white font-mono">{invoice.invoice_number}</td>
                      <td className="p-4 text-[#A1A1AA]">{invoice.description}</td>
                      <td className="p-4 text-white font-semibold">
                        ${invoice.amount.toLocaleString()}
                      </td>
                      <td className="p-4 text-[#A1A1AA]">
                        {new Date(invoice.due_date).toLocaleDateString()}
                      </td>
                      <td className="p-4">{getStatusBadge(invoice.status)}</td>
                      <td className="p-4">
                        {invoice.status !== "paid" ? (
                          <Link
                            to={`/portal/invoices/${invoice.id}`}
                            className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2"
                            data-testid={`pay-invoice-${invoice.id}`}
                          >
                            <CreditCard size={16} />
                            Pay Now
                          </Link>
                        ) : (
                          <span className="text-green-400 text-sm flex items-center gap-1">
                            <CheckCircle size={16} />
                            Paid
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
